import { apiClient } from '../utils/apiClient';
import type {
  LetterTemplate,
  LetterTemplateListResponse,
  LetterTemplateSearchOptions,
  PopulateResult,
  LetterSettings,
} from '../types/letterTemplate.types';

const BASE_PATH = '/api/letter-templates';

export const letterTemplateService = {
  /** List letter templates with optional filters */
  async getTemplates(options?: LetterTemplateSearchOptions): Promise<LetterTemplateListResponse> {
    const params = new URLSearchParams();
    if (options?.search) params.set('search', options.search);
    if (options?.category) params.set('category', options.category);
    if (options?.isActive !== undefined) params.set('isActive', String(options.isActive));
    if (options?.limit) params.set('limit', String(options.limit));
    if (options?.offset) params.set('offset', String(options.offset));
    if (options?.sortBy) params.set('sortBy', options.sortBy);
    if (options?.sortOrder) params.set('sortOrder', options.sortOrder);

    const query = params.toString();
    return apiClient.get<LetterTemplateListResponse>(`${BASE_PATH}${query ? `?${query}` : ''}`);
  },

  /** Get a single template by ID */
  async getTemplate(id: string): Promise<{ template: LetterTemplate }> {
    return apiClient.get<{ template: LetterTemplate }>(`${BASE_PATH}/${id}`);
  },

  /** Create a template from HTML content */
  async createTemplate(data: {
    title: string;
    htmlContent: string;
    description?: string;
    category?: string;
    letterSettings?: LetterSettings;
  }): Promise<{ template: LetterTemplate }> {
    return apiClient.post<{ template: LetterTemplate }>(BASE_PATH, data);
  },

  /** Upload a .docx or .pdf file as a letter template */
  async uploadTemplate(
    file: File,
    title?: string,
    description?: string,
    category?: string,
  ): Promise<{ template: LetterTemplate }> {
    const formData = new FormData();
    formData.append('file', file);
    if (title) formData.append('title', title);
    if (description) formData.append('description', description);
    if (category) formData.append('category', category);

    return apiClient.post<{ template: LetterTemplate }>(`${BASE_PATH}/upload`, formData);
  },

  /** Update a template */
  async updateTemplate(
    id: string,
    data: {
      title?: string;
      htmlContent?: string;
      description?: string;
      category?: string;
      isActive?: boolean;
      letterSettings?: LetterSettings;
    },
  ): Promise<{ template: LetterTemplate }> {
    return apiClient.put<{ template: LetterTemplate }>(`${BASE_PATH}/${id}`, data);
  },

  /** Soft-delete a template */
  async deleteTemplate(id: string): Promise<void> {
    await apiClient.delete(`${BASE_PATH}/${id}`);
  },

  /** Increment usage count */
  async incrementUsage(id: string): Promise<void> {
    await apiClient.post(`${BASE_PATH}/${id}/use`);
  },

  /** Get available categories */
  async getCategories(): Promise<{ categories: string[] }> {
    return apiClient.get<{ categories: string[] }>(`${BASE_PATH}/categories`);
  },

  /** Auto-populate a template with report data */
  async populateTemplate(
    id: string,
    reportData: Record<string, any>,
  ): Promise<PopulateResult> {
    return apiClient.post<PopulateResult>(`${BASE_PATH}/${id}/populate`, { reportData });
  },
};
